import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Spinner } from 'react-bootstrap';
import {
    FocGapAuditNotes,
    FocGapForm,
    FocGapModalFooter,
    FocGapNotes,
    ModalTabs,
} from '~/components/Patient';
import { useRoleActions } from '~/util';
import { ServicesContext } from '~/services';
import { BSModal } from '~/components/Bootstrap';
import { FocGapReminders } from '~/components/Reminders';
import { useParseHyperlinkText } from '~/util/useParseHyperlinkText';

const propTypes = {
    focGap: PropTypes.shape({
        measure: PropTypes.shape({
            focs: PropTypes.arrayOf(
                PropTypes.shape({
                    instruction: PropTypes.string,
                    type: PropTypes.string,
                })
            ),
            measure_category: PropTypes.shape({
                id: PropTypes.number,
                description: PropTypes.string,
            }),
            instructions: PropTypes.string,
        }),
        id: PropTypes.number.isRequired,
        status: PropTypes.number.isRequired,
        submission_answer: PropTypes.shape({
            id: PropTypes.number,
            status: PropTypes.number.isRequired,
        }),
        type: PropTypes.number,
        updated_at: PropTypes.string,
    }).isRequired,
    patientId: PropTypes.number.isRequired,
    handleClose: PropTypes.func.isRequired,
    // eslint-disable-next-line react/require-default-props
    defaultTab: PropTypes.string,
    // eslint-disable-next-line react/require-default-props
    addReminderOpen: PropTypes.bool,
};

function FocGapModal({
    focGap,
    patientId,
    handleClose,
    defaultTab = null,
    addReminderOpen = false,
}) {
    const context = React.useContext(ServicesContext);
    const rolePermission = useRoleActions('allow_save_edit_answer_foc_gap');
    const parseHyperlinkText = useParseHyperlinkText();
    const [tab, setTab] = React.useState('Measures');
    const [loading, setLoading] = React.useState(false);
    const [formDirty, setFormDirty] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [disabled, setDisabled] = React.useState(false);

    const tabs = ['Measures', 'Reminders', 'Notes', 'History'];
    if (focGap.measure?.instructions) tabs.splice(1, 0, 'Rationale');
    React.useEffect(() => {
        if (defaultTab) {
            setTab(defaultTab);
        }
    }, [defaultTab]);

    React.useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                await context.patient.getGap(focGap.id, focGap.type);
            } catch (e) {
                // empty catch
            } finally {
                setLoading(false);
            }
        })();
    }, [context.patient, focGap.id, focGap.type]);

    const formId = `foc_gap_form_${focGap.id}`;

    return (
        <BSModal
            show
            onHide={handleClose}
            className="attestation-modal-card gap-modal foc-gap-modal"
            aria-labelledby={`pcm-foc-${focGap.id}`}
        >
            <Modal.Header closeButton>
                <Modal.Title id={`pcm-foc-${focGap.id}`} role="heading" aria-level="1">
                    <div>{focGap.measure.focs?.[0]?.type}</div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ModalTabs tab={tab} tabs={tabs} setTab={setTab} />
                {loading && ['Info', 'Measures'].includes(tab) && (
                    <div className="text-center">
                        <Spinner animation="border" />
                    </div>
                )}
                {tab === 'Info' && !loading && (
                    <div className="large-modal-scroll">
                        <p>
                            <strong>Focus Type</strong>
                            <br />
                            {focGap.measure.focs.map((x) => x?.type)}
                        </p>
                        <p>
                            <strong>Focus Category</strong>
                            <br />
                            {focGap.measure.measure_category.description}
                        </p>
                        <p>
                            <strong>Focus Instruction</strong>
                            <br />
                            {focGap.measure.focs.map((x) => x.instruction)}
                        </p>
                    </div>
                )}
                <div className={tab === 'Measures' && !loading ? 'large-modal-scroll' : 'd-none'}>
                    {!rolePermission && (
                        <div className="permissions-alert">
                            You don’t have permission to save responses on this record.
                        </div>
                    )}
                    <div className="mb-2">
                        <div>
                            <b>Instructions</b>
                        </div>
                        <div className="ms-2">
                            {focGap.measure.focs.map((foc) => foc?.instruction).join('') || 'None'}
                        </div>
                    </div>
                    <FocGapForm
                        focGap={focGap}
                        formId={formId}
                        patientId={patientId}
                        setFormDirty={setFormDirty}
                        setSaving={setSaving}
                        setDisabled={setDisabled}
                        onSubmitSuccess={handleClose}
                    />
                </div>
                {tab === 'Rationale' && (
                    <div className="large-modal-scroll">
                        <div className="help-instructions-text">
                            {parseHyperlinkText(focGap.measure.instructions)}
                        </div>
                    </div>
                )}
                {tab === 'Reminders' && (
                    <FocGapReminders
                        focGapId={focGap.id}
                        patientId={patientId}
                        addReminderOpen={addReminderOpen}
                    />
                )}
                {tab === 'Notes' && <FocGapNotes entityId={focGap.id} patientId={patientId} />}
                {tab === 'History' && <FocGapAuditNotes entityId={focGap.id} gap={focGap} />}
            </Modal.Body>
            <Modal.Footer>
                <FocGapModalFooter
                    formId={formId}
                    saving={saving}
                    rolePermission={rolePermission}
                    formDirty={formDirty}
                    tab={tab}
                    patientId={patientId}
                    setDisabled={setDisabled}
                    handleClose={handleClose}
                    disabled={disabled}
                    focGap={focGap}
                />
            </Modal.Footer>
        </BSModal>
    );
}

FocGapModal.propTypes = propTypes;

export { FocGapModal };
